// DEPLOY: /var/www/woody.wtf/frontend/src/pages/MarketScanner.js
// Stock/ETF scanner. Mobile-friendly; links up to the desktop Workspace.
// dividend_yield is already a % (snapshot_equities, calc_performance.py) - shown
// and filtered as is; MER is a fraction (x100).
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import Navbar from '../components/Navbar';
import api from '../api';

// ── Constants ────────────────────────────────────────────────────────────────
const EMA_OPTIONS = ['8','9','12','20','21','26','50','70','100','150','200'];
const PERF_PERIODS = ['1w','1m','3m','6m','1y','2y','3y','5y','10y','20y'];
const PERF_PERIOD_YEARS = { '1w':1/52,'1m':1/12,'3m':0.25,'6m':0.5,'1y':1,'2y':2,'3y':3,'5y':5,'10y':10,'20y':20 };
const SCROLL_STEP = 120;
const FILTERABLE_COLS = new Set(['type','sector','industry','industry_group','sub_industry','exchange']);

const BASIC_COL_DEFS = [
  { key:'type',           label:'Type'        },
  { key:'sector',         label:'Sector'      },
  { key:'industry',       label:'Industry'    },
  { key:'industry_group', label:'Industry Group' },
  { key:'sub_industry',   label:'Sub-Industry'  },
  { key:'idx_tracked',    label:'Index'       },
  { key:'provider_name',  label:'Provider'    },
  { key:'expense_ratio',  label:'MER %'       },
  { key:'market_cap',     label:'Market Cap'  },
  { key:'last_price',     label:'Price'       },
  { key:'pe_ratio',       label:'P/E'         },
  { key:'dividend_yield', label:'Div Yield'   },
  { key:'volume',         label:'Volume'      },
  { key:'gearing_type',   label:'Gearing'     },
  { key:'gearing_ratio',  label:'Gear Ratio'  },
  { key:'description',    label:'Description' },
];

// ── Market cap tiers ─────────────────────────────────────────────────────────
const MCAP_TIERS_US = [
  { key:'mega',  label:'Mega',  min:200e9, max:Infinity },
  { key:'large', label:'Large', min:10e9,  max:200e9   },
  { key:'mid',   label:'Mid',   min:2e9,   max:10e9    },
  { key:'small', label:'Small', min:300e6, max:2e9     },
  { key:'micro', label:'Micro', min:50e6,  max:300e6   },
  { key:'nano',  label:'Nano',  min:0,     max:50e6    },
];
const MCAP_TIERS_ASX = [
  { key:'large', label:'Large', min:10e9,  max:Infinity },
  { key:'mid',   label:'Mid',   min:1e9,   max:10e9    },
  { key:'small', label:'Small', min:300e6, max:1e9     },
  { key:'micro', label:'Micro', min:50e6,  max:300e6   },
  { key:'nano',  label:'Nano',  min:0,     max:50e6    },
];

function mcapTierLabel(tier) {
  const f = v => v>=1e12?(v/1e12).toFixed(0)+'T':v>=1e9?(v/1e9).toFixed(0)+'B':v>=1e6?(v/1e6).toFixed(0)+'M':'0';
  return tier.max===Infinity ? '>'+f(tier.min) : f(tier.min)+'–'+f(tier.max);
}

// ── Default per-tab state ────────────────────────────────────────────────────
function defaultTabState(id) {
  return {
    _id: id,
    basicCols:    ['type','sector','expense_ratio','market_cap','last_price'],
    perfCols:     ['1m','6m','1y','5y'],
    perfType:     'drip',
    searchRules:  [{ id:0, connector:'AND', field:'all', op:'contains', value:'', caseSensitive:false }],
    filters: {
      mer_min:'',mer_max:'',mcap_min:'',mcap_max:'',
      price_min:'',price_max:'',rsi_min:'',rsi_max:'',
      stoch_min:'',stoch_max:'',volume_min:'',
      yield_min:'',yield_max:'',bb_position:'',
      gearing_types: new Set(['standard','fixed_leveraged','fixed_inverse','floating_leveraged']),
    },
    mcapMode:     'range',
    mcapTierDefs: 'us',
    mcapExcluded: new Set(),
    colExcluded:  {},
    emaRules:     [],
    priceEmaRules:[],
    sort:         { col:id==='holdings'?'holding_local':'symbol', dir:id==='holdings'?'DESC':'ASC' },
    page:         1,
    rowsPerPage:  50,
    advOpen:      false,
  };
}

// ── Styles ───────────────────────────────────────────────────────────────────
const S = {
  page:      (e) => ({ height:e?'100%':'auto', minHeight:e?0:'100vh', background:'var(--bg)', display:'flex', flexDirection:'column', overflow:e?'hidden':'visible' }),
  nav:       { background:'var(--surface)', borderBottom:'1px solid var(--border)', padding:'0 20px', height:'52px', display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0 },
  logo:      { fontFamily:"'JetBrains Mono',monospace", fontSize:'12px', fontWeight:500, letterSpacing:'0.1em', color:'var(--accent)', textTransform:'uppercase' },
  navRight:  { display:'flex', alignItems:'center', gap:'12px', fontSize:'13px', color:'var(--text-muted)' },
  logoutBtn: { background:'none', border:'none', cursor:'pointer', color:'var(--accent)', fontSize:'13px', padding:'4px 10px' },
  body:      (e) => ({ flex:1, display:'flex', flexDirection:'column', overflow:e?'hidden':'auto', position:'relative' }),
  workspaceLink: { color:'var(--accent)', fontSize:'12px', textDecoration:'none', fontFamily:"'JetBrains Mono',monospace", fontWeight:600, padding:'4px 10px', border:'1px solid var(--accent)', borderRadius:'var(--radius)', whiteSpace:'nowrap' },

  // Tab strip
  tabStripOuter: { background:'var(--surface)', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'center', height:'38px', flexShrink:0, overflow:'hidden' },
  tabStripInner: { display:'flex', alignItems:'center', gap:'2px', overflowX:'auto', overflowY:'hidden', flex:1, height:'100%', padding:'0 4px', scrollbarWidth:'none', msOverflowStyle:'none' },
  scrollArrow:   { flexShrink:0, background:'var(--surface)', border:'none', cursor:'pointer', color:'var(--text-muted)', fontSize:'14px', height:'100%', padding:'0 6px', display:'flex', alignItems:'center' },
  tab: (a) => ({
    display:'flex', alignItems:'center', gap:'4px', padding:'4px 8px', borderRadius:'4px 4px 0 0',
    cursor:'pointer', fontSize:'11px', fontFamily:"'JetBrains Mono',monospace", fontWeight:a?700:500,
    background:a?'var(--bg)':'transparent', color:a?'var(--accent)':'var(--text-muted)',
    border:a?'1px solid var(--border)':'1px solid transparent',
    borderBottom:a?'1px solid var(--bg)':'1px solid transparent',
    whiteSpace:'nowrap', userSelect:'none', flexShrink:0,
  }),
  tabBtn: { background:'none', border:'none', cursor:'pointer', color:'var(--text-muted)', fontSize:'13px', padding:'1px 3px', borderRadius:'3px' },

  // List selector bar (below tab strip)
  listBar:     { background:'var(--surface)', borderBottom:'1px solid var(--border)', padding:'5px 12px', display:'flex', alignItems:'center', gap:'8px', flexShrink:0 },
  listBarLabel:{ fontSize:'11px', fontWeight:700, color:'var(--text-muted)', fontFamily:"'JetBrains Mono',monospace", textTransform:'uppercase', whiteSpace:'nowrap' },
  listBarBtn:  (active) => ({ padding:'3px 10px', fontSize:'11px', fontWeight:600, border:`1px solid ${active?'var(--accent)':'var(--border)'}`, borderRadius:'20px', cursor:'pointer', background:active?'var(--accent)':'var(--surface)', color:active?'var(--on-accent)':'var(--text-muted)', fontFamily:"'JetBrains Mono',monospace", userSelect:'none', whiteSpace:'nowrap' }),
  listBarEmpty:{ fontSize:'12px', color:'var(--text-muted)', fontStyle:'italic' },

  advHeader:  { background:'var(--bg)', border:'1px solid var(--border)', borderRadius:'var(--radius)', padding:'10px 16px', cursor:'pointer', display:'flex', justifyContent:'space-between', alignItems:'center', fontWeight:600, fontSize:'13px', color:'var(--text)', margin:'8px 12px 0', flexShrink:0 },
  advContent: { background:'var(--surface)', border:'1px solid var(--border)', borderTop:'none', borderRadius:'0 0 var(--radius) var(--radius)', padding:'16px', display:'flex', flexDirection:'column', gap:'16px', margin:'0 12px', flexShrink:0, overflowY:'auto', maxHeight:'60vh' },

  colToggle:  (a) => ({ padding:'4px 10px', fontSize:'11px', fontWeight:600, border:`2px solid ${a?'var(--accent)':'var(--border)'}`, borderRadius:'20px', cursor:'pointer', background:a?'var(--accent)':'var(--surface)', color:a?'var(--on-accent)':'var(--text-muted)', fontFamily:"'JetBrains Mono',monospace", userSelect:'none' }),
  colGrid:    { display:'flex', flexWrap:'wrap', gap:'6px' },

  filterInput: { padding:'5px 8px', fontSize:'12px', border:'1px solid var(--border)', borderRadius:'var(--radius)', background:'var(--bg)', color:'var(--text)', fontFamily:"'JetBrains Mono',monospace", width:'80px' },
  filterFull:  { padding:'5px 8px', fontSize:'12px', border:'1px solid var(--border)', borderRadius:'var(--radius)', background:'var(--bg)', color:'var(--text)', fontFamily:"'JetBrains Mono',monospace", width:'100%' },
  filterSelect:{ padding:'5px 8px', fontSize:'12px', border:'1px solid var(--border)', borderRadius:'var(--radius)', background:'var(--bg)', color:'var(--text)', fontFamily:"'JetBrains Mono',monospace" },
  filterGrid:  { display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(200px, 1fr))', gap:'12px' },
  filterLabel: { fontSize:'11px', fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', fontFamily:"'JetBrains Mono',monospace", display:'block', marginBottom:'4px' },
  rangeRow:    { display:'flex', gap:'5px', alignItems:'center' },

  ruleRow:    { display:'flex', gap:'6px', alignItems:'center', marginBottom:'6px', flexWrap:'wrap' },
  ruleSelect: { padding:'4px 6px', fontSize:'11px', border:'1px solid var(--border)', borderRadius:'var(--radius)', background:'var(--bg)', color:'var(--text)', fontFamily:"'JetBrains Mono',monospace" },
  removeBtn:  { padding:'2px 7px', border:'none', background:'var(--error)', color:'var(--on-accent)', borderRadius:'3px', cursor:'pointer', fontSize:'11px' },
  addRuleBtn: { padding:'3px 10px', fontSize:'11px', border:'2px solid var(--accent)', borderRadius:'20px', background:'var(--accent)', color:'var(--on-accent)', cursor:'pointer', fontFamily:"'JetBrains Mono',monospace", fontWeight:600 },

  btn:         { background:'var(--accent)', color:'var(--on-accent)', border:'none', borderRadius:'var(--radius)', padding:'7px 18px', fontSize:'13px', fontWeight:600, cursor:'pointer' },
  btnSm:       { background:'var(--accent)', color:'var(--on-accent)', border:'none', borderRadius:'var(--radius)', padding:'4px 12px', fontSize:'12px', fontWeight:600, cursor:'pointer' },
  btnSecondary:{ background:'var(--bg)', color:'var(--text-muted)', border:'1px solid var(--border)', borderRadius:'var(--radius)', padding:'7px 18px', fontSize:'13px', cursor:'pointer' },
  btnGhost:    { background:'none', color:'var(--text-muted)', border:'1px solid var(--border)', borderRadius:'var(--radius)', padding:'4px 10px', fontSize:'12px', cursor:'pointer' },

  tableArea:  { flex:1, overflow:'auto', padding:'8px 12px 12px', display:'flex', flexDirection:'column', gap:'8px', minHeight:0 },
  statsBar:   { display:'flex', alignItems:'center', justifyContent:'space-between', fontSize:'13px', color:'var(--text-muted)', flexShrink:0 },
  tableWrap:  { flex:1, overflow:'auto', border:'1px solid var(--border)', borderRadius:'var(--radius)', background:'var(--surface)', minHeight:0 },
  table:      { width:'100%', borderCollapse:'collapse', fontSize:'12px', fontFamily:"'JetBrains Mono',monospace" },
  th:         { padding:'8px 10px', textAlign:'left', borderBottom:'2px solid var(--border)', background:'var(--accent)', color:'var(--on-accent)', position:'sticky', top:0, zIndex:10, cursor:'pointer', userSelect:'none', whiteSpace:'nowrap', fontSize:'11px', fontWeight:700, textTransform:'uppercase' },
  thCheck:    { padding:'8px 10px', textAlign:'center', borderBottom:'2px solid var(--border)', background:'var(--accent)', color:'var(--on-accent)', position:'sticky', top:0, zIndex:10, width:'36px' },
  td:         { padding:'7px 10px', borderBottom:'1px solid var(--border)', whiteSpace:'nowrap' },
  tdCheck:    { padding:'7px 10px', borderBottom:'1px solid var(--border)', textAlign:'center', width:'36px' },
  positive:   { color:'var(--up)', fontWeight:600 },
  negative:   { color:'var(--down)', fontWeight:600 },
  badge:      { fontSize:'10px', padding:'2px 6px', borderRadius:'3px', fontWeight:700 },
  pagination: { display:'flex', alignItems:'center', gap:'6px', justifyContent:'space-between', padding:'4px 0', flexShrink:0, flexWrap:'wrap' },
  pageBtn:    (a) => ({ padding:'3px 9px', fontSize:'12px', border:'1px solid var(--border)', borderRadius:'4px', cursor:'pointer', background:a?'var(--accent)':'var(--surface)', color:a?'var(--on-accent)':'var(--text)', fontFamily:"'JetBrains Mono',monospace" }),
  loading:    { display:'flex', alignItems:'center', justifyContent:'center', padding:'60px', fontSize:'13px', color:'var(--text-muted)', gap:'10px' },
  missingNote:{ background:'var(--warning-dim)', border:'1px solid var(--border)', borderRadius:'4px', padding:'6px 12px', fontSize:'12px', color:'var(--warning)', flexShrink:0 },
  selBar:     { display:'flex', alignItems:'center', gap:'12px', background:'var(--accent-dim)', border:'1px solid var(--accent)', borderRadius:'var(--radius)', padding:'8px 12px', fontSize:'13px', flexShrink:0 },
  selCount:   { fontWeight:600, color:'var(--accent)', fontFamily:"'JetBrains Mono',monospace" },

  colDropdown: { position:'absolute', top:'100%', left:0, minWidth:'220px', background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--radius)', boxShadow:'0 4px 16px rgba(0,0,0,0.15)', zIndex:200, padding:'8px 0' },
  colDropItem: { display:'flex', alignItems:'center', gap:'8px', padding:'5px 12px', cursor:'pointer', fontSize:'12px', fontFamily:"'JetBrains Mono',monospace" },

  listSelectorBtn: { flexShrink:0, background:'none', border:'1px solid var(--border)', borderRadius:'4px', cursor:'pointer', color:'var(--text-muted)', fontSize:'11px', fontFamily:"'JetBrains Mono',monospace", padding:'3px 8px', whiteSpace:'nowrap', display:'flex', alignItems:'center', gap:'4px' },
  listDropdown: { position:'absolute', minWidth:'220px', background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--radius)', boxShadow:'0 4px 16px rgba(0,0,0,0.2)', zIndex:500, padding:'8px 0' },
  listDropItem: { display:'flex', alignItems:'center', gap:'8px', padding:'5px 12px', cursor:'pointer', fontSize:'12px', fontFamily:"'JetBrains Mono',monospace" },
  modalOverlay:{ position:'fixed', inset:0, background: 'var(--overlay)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center' },
  modal:       { background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'calc(var(--radius)*2)', padding:'28px', width:'420px', maxWidth:'95vw', boxShadow:'0 8px 32px rgba(0,0,0,0.2)' },
  modalTitle:  { fontSize:'16px', fontWeight:700, marginBottom:'16px', color:'var(--text)' },
  modalInput:  { width:'100%', padding:'7px 10px', fontSize:'13px', border:'1px solid var(--border)', borderRadius:'var(--radius)', background:'var(--bg)', color:'var(--text)', fontFamily:"'Space Grotesk',sans-serif", boxSizing:'border-box', marginTop:'4px' },
  modalLabel:  { fontSize:'11px', fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', fontFamily:"'JetBrains Mono',monospace", display:'block', marginBottom:'4px' },
  modalSelect: { width:'100%', padding:'7px 10px', fontSize:'13px', border:'1px solid var(--border)', borderRadius:'var(--radius)', background:'var(--bg)', color:'var(--text)', fontFamily:"'JetBrains Mono',monospace", boxSizing:'border-box' },
  modalFooter: { display:'flex', gap:'8px', justifyContent:'flex-end', marginTop:'20px' },
  modalError:  { color:'var(--error)', fontSize:'12px', marginTop:'6px' },
  modalSuccess:{ color:'var(--success)', fontSize:'12px', marginTop:'6px' },
};

const KEYFRAMES = `@keyframes spin { to { transform: rotate(360deg); } }`;

// ── Helpers ──────────────────────────────────────────────────────────────────
function fmt(v,dp=2)  { return v==null?'-':Number(v).toFixed(dp); }
function fmtPrice(v)  { if(v==null)return '-'; const p=Number(v); if(p>=10)return '$'+p.toFixed(2); if(p>=1)return '$'+p.toFixed(3); if(p>=0.01)return '$'+p.toFixed(4); return '$'+p.toFixed(6); }
function fmtMktCap(v) { if(v==null)return '-'; if(v>=1e12)return '$'+(v/1e12).toFixed(2)+'T'; if(v>=1e9)return '$'+(v/1e9).toFixed(2)+'B'; if(v>=1e6)return '$'+(v/1e6).toFixed(0)+'M'; return '$'+v.toFixed(0); }
function fmtMer(v)    { return v==null?'-':(v*100).toFixed(2)+'%'; }
function fmtVol(v)    { return v==null?'-':Number(v).toLocaleString(); }
function emaKey(n)    { return 'ema'+String(n).padStart(2,'0'); }

// Table rows are keyed by symbol:exchange — drop dupes or React renders stale/ghost rows
function dedupeRows(rows) {
  const seen=new Set();
  return rows.filter(r=>{
    const k=`${r.symbol}:${r.exchange}`;
    if(seen.has(k))return false;
    seen.add(k);return true;
  });
}

function annualize(cumPct, period) {
  const y=PERF_PERIOD_YEARS[period];
  if(cumPct==null)return null;
  if(y<=1)return cumPct;
  const dec=cumPct/100;
  if(dec<=-0.99||dec>=1000)return null;
  const val=(Math.pow(1+dec,1/y)-1)*100;
  return isFinite(val)&&Math.abs(val)<100000?val:null;
}

// ── ColFilterDropdown ─────────────────────────────────────────────────────────
function ColFilterDropdown({ values, excluded, onToggle, onClear, onClose }) {
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if(ref.current&&!ref.current.contains(e.target))onClose(); };
    document.addEventListener('mousedown',h);
    return ()=>document.removeEventListener('mousedown',h);
  },[onClose]);

  const sorted = [...values].sort();
  const allOn  = excluded.size===0;

  return (
    <div ref={ref} style={S.colDropdown} onClick={e=>e.stopPropagation()}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'4px 12px 8px',borderBottom:'1px solid var(--border)',marginBottom:'4px'}}>
        <span style={{fontSize:'11px',fontWeight:700,color:'var(--text-muted)',fontFamily:"'JetBrains Mono',monospace",textTransform:'uppercase'}}>Filter</span>
        <div style={{display:'flex',gap:'6px'}}>
          <button style={{...S.btnGhost,padding:'2px 8px',fontSize:'11px'}} onClick={()=>allOn?onToggle('__DESELECT_ALL__',sorted):onClear()}>
            {allOn?'Deselect All':'Select All'}
          </button>
          {!allOn&&<button style={{...S.btnGhost,padding:'2px 8px',fontSize:'11px'}} onClick={onClear}>Clear</button>}
        </div>
      </div>
      <div style={{maxHeight:'260px',overflowY:'auto'}}>
        {sorted.map(v=>{
          const checked=!excluded.has(v);
          return (
            <label key={v} style={{...S.colDropItem,background:checked?'transparent':'var(--neg-faint)'}}>
              <input type="checkbox" checked={checked} onChange={()=>onToggle(v)} style={{cursor:'pointer'}} />
              <span style={{color:checked?'var(--text)':'var(--text-muted)'}}>{v||'(empty)'}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
}

// ── AddToListModal ────────────────────────────────────────────────────────────
function AddToListModal({ tickers, onClose, onListCreated }) {
  const [lists,setLists]=useState([]);
  const [mode,setMode]=useState('existing');
  const [selListId,setSelListId]=useState('');
  const [newName,setNewName]=useState('');
  const [busy,setBusy]=useState(false);
  const [msg,setMsg]=useState('');
  const [err,setErr]=useState('');

  useEffect(()=>{
    api.get('/lists').then(r=>{
      const l=r.data.lists||[];
      setLists(l);
      if(l.length>0)setSelListId(String(l[0].id));
      else setMode('new');
    }).catch(()=>setMode('new'));
  },[]);

  const submit=async()=>{
    setBusy(true);setErr('');setMsg('');
    try{
      let listId;
      if(mode==='new'){
        if(!newName.trim()){setErr('List name required');setBusy(false);return;}
        const r=await api.post('/lists',{name:newName.trim()});
        listId=r.data.list.id;
        if(onListCreated)onListCreated(r.data.list);
      }else{
        listId=parseInt(selListId);
        if(!listId){setErr('Select a list');setBusy(false);return;}
      }
      await Promise.all(tickers.map(t=>api.post(`/lists/${listId}/items`,{ticker:t}).catch(e=>{if(e.response?.status!==409)throw e;})));
      setMsg(`Added ${tickers.length} ticker${tickers.length!==1?'s':''} to list`);
      setTimeout(()=>onClose(),1500);
    }catch(e){setErr(e.response?.data?.error||'Failed');}
    finally{setBusy(false);}
  };

  return (
    <div style={S.modalOverlay} onClick={e=>{if(e.target===e.currentTarget)onClose();}}>
      <div style={S.modal}>
        <div style={S.modalTitle}>Add {tickers.length} ticker{tickers.length!==1?'s':''} to list</div>
        {lists.length>0&&(
          <div style={{display:'flex',gap:'8px',marginBottom:'14px'}}>
            <button style={{...S.btnSm,background:mode==='existing'?'var(--accent)':'var(--bg)',color:mode==='existing'?'var(--on-accent)':'var(--text-muted)',border:'1px solid var(--border)'}} onClick={()=>setMode('existing')}>Existing</button>
            <button style={{...S.btnSm,background:mode==='new'?'var(--accent)':'var(--bg)',color:mode==='new'?'var(--on-accent)':'var(--text-muted)',border:'1px solid var(--border)'}} onClick={()=>setMode('new')}>New</button>
          </div>
        )}
        {mode==='existing'&&lists.length>0?(
          <div><label style={S.modalLabel}>Select List</label>
            <select style={S.modalSelect} value={selListId} onChange={e=>setSelListId(e.target.value)}>
              {lists.map(l=><option key={l.id} value={l.id}>{l.name} ({l.item_count})</option>)}
            </select></div>
        ):(
          <div><label style={S.modalLabel}>New List Name</label>
            <input style={S.modalInput} value={newName} onChange={e=>setNewName(e.target.value)} placeholder="e.g. ASX Watchlist" maxLength={100} /></div>
        )}
        {err&&<div style={S.modalError}>{err}</div>}
        {msg&&<div style={S.modalSuccess}>{msg}</div>}
        <div style={S.modalFooter}>
          <button style={S.btnGhost} onClick={onClose} disabled={busy}>Cancel</button>
          <button style={S.btn} onClick={submit} disabled={busy}>{busy?'Adding…':'Add to List'}</button>
        </div>
      </div>
    </div>
  );
}

// ── TabStrip ──────────────────────────────────────────────────────────────────
function TabStrip({ tabs, activeTab, onSelect, onClose, onRefresh }) {
  const innerRef = useRef(null);
  const [canLeft,  setCanLeft]  = useState(false);
  const [canRight, setCanRight] = useState(false);

  const checkOverflow = useCallback(()=>{
    const el=innerRef.current;
    if(!el)return;
    setCanLeft(el.scrollLeft>0);
    setCanRight(el.scrollLeft+el.clientWidth<el.scrollWidth-1);
  },[]);

  useEffect(()=>{
    checkOverflow();
    const el=innerRef.current;
    if(!el)return;
    el.addEventListener('scroll',checkOverflow,{passive:true});
    const ro=new ResizeObserver(checkOverflow);
    ro.observe(el);
    return()=>{el.removeEventListener('scroll',checkOverflow);ro.disconnect();};
  },[tabs,checkOverflow]);

  const scroll=(dir)=>{const el=innerRef.current;if(el)el.scrollBy({left:dir*SCROLL_STEP,behavior:'smooth'});};

  return (
    <div style={S.tabStripOuter}>
      {canLeft&&<button style={{...S.scrollArrow,borderRight:'1px solid var(--border)'}} onClick={()=>scroll(-1)}>‹</button>}
      <div ref={innerRef} style={S.tabStripInner}>
        {tabs.map(t=>(
          <div key={t.id} style={S.tab(t.id===activeTab)} onClick={()=>onSelect(t.id)}>
            <button style={S.tabBtn} title="Refresh" onClick={e=>{e.stopPropagation();onRefresh(t.id);}}>↻</button>
            <span>{t.label}</span>
            {t.id!=='total'&&t.id!=='holdings'&&<button style={S.tabBtn} title="Close" onClick={e=>{e.stopPropagation();onClose(t.id);}}>×</button>}
          </div>
        ))}
      </div>
      {canRight&&<button style={{...S.scrollArrow,borderLeft:'1px solid var(--border)'}} onClick={()=>scroll(1)}>›</button>}
    </div>
  );
}

// ── ListSelector bar ──────────────────────────────────────────────────────────
// Renders below the tab strip — pill buttons for each list, active = loaded as tab
function ListSelector({ lists, loadedListIds, onToggleList }) {
  const [open, setOpen] = useState(false);
  const btnRef  = useRef(null);
  const dropRef = useRef(null);

  useEffect(()=>{
    const h=(e)=>{
      if(btnRef.current&&!btnRef.current.contains(e.target)&&
         dropRef.current&&!dropRef.current.contains(e.target)){setOpen(false);}
    };
    document.addEventListener('mousedown',h);
    return()=>document.removeEventListener('mousedown',h);
  },[]);

  return (
    <div style={{...S.listBar, position:'relative'}}>
      <button ref={btnRef} style={S.listSelectorBtn} onClick={()=>setOpen(v=>!v)}>
        📋 Lists{loadedListIds.size>0?` (${loadedListIds.size})`:''} <span style={{fontSize:'9px'}}>▾</span>
      </button>
      {open&&(
        <div ref={dropRef} style={{...S.listDropdown, top:'100%', left:0, right:'auto'}}>
          <div style={{padding:'4px 12px 8px',borderBottom:'1px solid var(--border)',marginBottom:'4px',fontSize:'11px',fontWeight:700,color:'var(--text-muted)',fontFamily:"'JetBrains Mono',monospace",textTransform:'uppercase'}}>Your Lists</div>
          {lists.length===0
            ?<div style={{padding:'8px 12px',fontSize:'12px',color:'var(--text-muted)'}}>No lists created yet.</div>
            :lists.map(l=>(
              <label key={l.id} style={S.listDropItem}>
                <input type="checkbox" checked={loadedListIds.has(l.id)} onChange={()=>onToggleList(l)} style={{cursor:'pointer'}} />
                <span style={{color:'var(--text)'}}>{l.name}</span>
                <span style={{fontSize:'10px',color:'var(--text-muted)',marginLeft:'auto'}}>{l.item_count}</span>
              </label>
            ))
          }
        </div>
      )}
    </div>
  );
}

// ── ScannerView ───────────────────────────────────────────────────────────────
function ScannerView({ allData, listTickers, tabState, onStateChange, onTickerClick, onListCreated, ruleIdRef, holdingsMode=false, onToggleHolding=null }) {
  const {
    _id, basicCols, perfCols, perfType, searchRules,
    filters, mcapMode, mcapTierDefs, mcapExcluded,
    colExcluded, emaRules, priceEmaRules,
    sort, page, rowsPerPage, advOpen,
  } = tabState;

  const set = (patch) => onStateChange(prev=>({...prev,...patch}));
  const setF = (k,v) => set({filters:{...filters,[k]:v}});
  const mcapTiers = mcapTierDefs==='us'?MCAP_TIERS_US:MCAP_TIERS_ASX;

  const [colDropOpen,  setColDropOpen]  = useState(null);
  const [selected,     setSelected]     = useState(new Set());
  const [showModal,    setShowModal]    = useState(false);
  const [missingCount, setMissingCount] = useState(0);

  // ── Filter ─────────────────────────────────────────────────────────────────
  const filtered = useMemo(()=>{
    let miss=0;
    const f=filters;
    const base=listTickers?allData.filter(r=>listTickers.has(`${r.symbol}:${r.exchange}`)):allData;

    const result=base.filter(row=>{
      if(!holdingsMode&&row.asset_type!=='equity'&&row.asset_type!=='etf')return false;

      if(searchRules.some(r=>r.value)){
        let res=null;
        for(let i=0;i<searchRules.length;i++){
          const r=searchRules[i];
          if(!r.value)continue;
          const fields=r.field==='all'
            ?[row.symbol,row.name,row.sector,row.industry,row.industry_group,row.sub_industry,row.idx_tracked,row.provider_name,row.description]
            :[row[r.field]||''];
          const val=r.caseSensitive?r.value:r.value.toLowerCase();
          let match=false;
          for(const fv of fields){
            if(!fv)continue;
            const fvs=r.caseSensitive?fv:fv.toLowerCase();
            if(r.op==='contains'&&fvs.includes(val))match=true;
            else if(r.op==='equals'&&fvs===val)match=true;
            else if(r.op==='starts'&&fvs.startsWith(val))match=true;
            else if(r.op==='ends'&&fvs.endsWith(val))match=true;
            if(match)break;
          }
          if(i===0)res=r.connector==='NOT'?!match:match;
          else if(r.connector==='AND')res=res&&match;
          else if(r.connector==='OR')res=res||match;
          else if(r.connector==='NOT')res=res&&!match;
        }
        if(res===false)return false;
      }

      const inR=(v,mn,mx)=>{
        if(mn===''&&mx==='')return true;
        if(v==null)return false;
        if(mn!==''&&!isNaN(mn)&&v<parseFloat(mn))return false;
        if(mx!==''&&!isNaN(mx)&&v>parseFloat(mx))return false;
        return true;
      };

      const merPct=row.expense_ratio!=null?row.expense_ratio*100:null;
      if(!inR(merPct,f.mer_min,f.mer_max))return false;

      if(mcapMode==='range'){
        const mcapM=row.market_cap!=null?row.market_cap/1e6:null;
        if(!inR(mcapM,f.mcap_min,f.mcap_max))return false;
      }else if(mcapExcluded.size>0){
        const mc=row.market_cap;
        if(mc==null)return false;
        const tier=mcapTiers.find(t=>mc>=t.min&&mc<t.max);
        if(!tier||mcapExcluded.has(tier.key))return false;
      }

      if(holdingsMode && (!inR(row.holding_local,f.holding_min||'',f.holding_max||'')
        || !inR(row.holding_overall,f.overall_min||'',f.overall_max||'')))return false;
      if(!inR(row.last_price,f.price_min,f.price_max))return false;
      if(!inR(row.rsi14,f.rsi_min,f.rsi_max))return false;
      if(!inR(row.stoch_k,f.stoch_min,f.stoch_max))return false;
      if(f.volume_min!==''&&!isNaN(f.volume_min)&&(row.volume==null||row.volume<parseFloat(f.volume_min)))return false;
      const yp=row.dividend_yield;
      if(!inR(yp,f.yield_min,f.yield_max))return false;

      if(f.bb_position){
        const p=row.last_price,bu=row.bb_upper,bl=row.bb_lower;
        if(p==null||bu==null||bl==null)return false;
        if(f.bb_position==='above_upper'&&p<=bu)return false;
        if(f.bb_position==='below_lower'&&p>=bl)return false;
        if(f.bb_position==='inside'&&(p<=bl||p>=bu))return false;
      }

      // Gearing type filter — if the set doesn't include this row's gearing_type, exclude
      if(f.gearing_types&&f.gearing_types.size<4){
        const gt=row.gearing_type||'standard';
        if(!f.gearing_types.has(gt))return false;
      }

      for(const rule of emaRules){
        if(!rule.ema1||!rule.ema2)continue;
        const v1=row[emaKey(rule.ema1)],v2=row[emaKey(rule.ema2)];
        if(v1==null||v2==null){miss++;return false;}
        if(rule.dir==='above'&&v1<=v2)return false;
        if(rule.dir==='below'&&v1>=v2)return false;
      }
      for(const rule of priceEmaRules){
        if(!rule.ema)continue;
        const p=row.last_price,ev=row[emaKey(rule.ema)];
        if(p==null||ev==null){miss++;return false;}
        if(rule.dir==='above'&&p<=ev)return false;
        if(rule.dir==='below'&&p>=ev)return false;
      }
      return true;
    });
    setMissingCount(miss);
    return result;
  },[allData,listTickers,searchRules,filters,emaRules,priceEmaRules,mcapMode,mcapExcluded,mcapTiers,holdingsMode]);

  const colFiltered=useMemo(()=>{
    return filtered.filter(row=>{
      for(const[ck,excSet]of Object.entries(colExcluded)){
        if(!excSet||excSet.size===0)continue;
        const val=ck==='exchange'?row.exchange||'':ck==='type'?row.asset_type||'':row[ck]||'';
        if(excSet.has(val))return false;
      }
      return true;
    });
  },[filtered,colExcluded]);

  const colValues=useMemo(()=>{
    const v={exchange:new Set(),type:new Set(),sector:new Set(),industry:new Set(),industry_group:new Set(),sub_industry:new Set()};
    filtered.forEach(r=>{v.exchange.add(r.exchange||'');v.type.add(r.asset_type||'');v.sector.add(r.sector||'');v.industry.add(r.industry||'');v.industry_group.add(r.industry_group||'');v.sub_industry.add(r.sub_industry||'');});
    return v;
  },[filtered]);

  const sorted=useMemo(()=>{
    const compare=(a,b)=>{
      const av=getSortVal(a,sort.col,perfType),bv=getSortVal(b,sort.col,perfType);
      if(av==null&&bv==null)return 0;
      if(av==null)return 1;
      if(bv==null)return -1;
      const na=parseFloat(av),nb=parseFloat(bv);
      if(!isNaN(na)&&!isNaN(nb))return sort.dir==='ASC'?na-nb:nb-na;
      const sa=String(av).toLowerCase(),sb=String(bv).toLowerCase();
      return sort.dir==='ASC'?sa.localeCompare(sb):sb.localeCompare(sa);
    };
    if(!holdingsMode)return [...colFiltered].sort(compare);
    // Sort siblings without pulling nested holdings away from their ETF.
    const visible=new Set(colFiltered.map(r=>r._holdingPath));
    const groups=new Map(),result=[];
    colFiltered.forEach(r=>{
      const parent=visible.has(r._holdingParent)?r._holdingParent:'';
      if(!groups.has(parent))groups.set(parent,[]);
      groups.get(parent).push(r);
    });
    const visit=parent=>(groups.get(parent)||[]).sort(compare).forEach(r=>{
      result.push(r);
      visit(r._holdingPath);
    });
    visit('');
    return result;
  },[colFiltered,sort,perfType,holdingsMode]);

  function getSortVal(row,col,pt){
    if(col==='type')return row.asset_type;
    if(col==='exchange')return row.exchange;
    if(col.startsWith('perf_')){
      const period=col.replace('perf_','');
      const isAnn=pt.endsWith('_annualized');
      const base=isAnn?pt.replace('_annualized',''):pt;
      const raw=row[base+'_'+period];
      return isAnn?annualize(raw,period):raw;
    }
    return row[col];
  }

  const totalPages=Math.max(1,Math.ceil(sorted.length/rowsPerPage));
  const pageData=sorted.slice((page-1)*rowsPerPage,page*rowsPerPage);

  const handleSort=(col)=>set({sort:sort.col===col?{col,dir:sort.dir==='ASC'?'DESC':'ASC'}:{col,dir:'DESC'},page:1});
  const toggleBasicCol=(k)=>set({basicCols:basicCols.includes(k)?basicCols.filter(x=>x!==k):[...basicCols,k]});
  const togglePerfCol=(k)=>{const n=perfCols.includes(k)?perfCols.filter(x=>x!==k):[...perfCols,k];set({perfCols:PERF_PERIODS.filter(p=>n.includes(p))});};

  const clearFilters=()=>set({
    filters:{mer_min:'',mer_max:'',mcap_min:'',mcap_max:'',price_min:'',price_max:'',rsi_min:'',rsi_max:'',stoch_min:'',stoch_max:'',volume_min:'',yield_min:'',yield_max:'',bb_position:'',holding_min:'',holding_max:'',overall_min:'',overall_max:'',gearing_types:new Set(['standard','fixed_leveraged','fixed_inverse','floating_leveraged'])},
    searchRules:[{id:0,connector:'AND',field:'all',op:'contains',value:'',caseSensitive:false}],
    emaRules:[],priceEmaRules:[],mcapMode:'range',mcapExcluded:new Set(),colExcluded:{},
  });

  const toggleMcapTier=(key)=>{const n=new Set(mcapExcluded);n.has(key)?n.delete(key):n.add(key);set({mcapExcluded:n,page:1});};

  const toggleColExclude=(ck,val,allVals)=>{
    if(val==='__DESELECT_ALL__'){set({colExcluded:{...colExcluded,[ck]:new Set(allVals)},page:1});return;}
    const cur=colExcluded[ck]?new Set(colExcluded[ck]):new Set();
    cur.has(val)?cur.delete(val):cur.add(val);
    set({colExcluded:{...colExcluded,[ck]:cur},page:1});
  };
  const clearColExclude=(ck)=>{const n={...colExcluded};delete n[ck];set({colExcluded:n,page:1});};

  const addSR=()=>{const id=++ruleIdRef.current;set({searchRules:[...searchRules,{id,connector:'AND',field:'all',op:'contains',value:'',caseSensitive:false}]});};
  const remSR=(id)=>set({searchRules:searchRules.filter(r=>r.id!==id)});
  const updSR=(id,k,v)=>set({searchRules:searchRules.map(r=>r.id===id?{...r,[k]:v}:r)});

  const addEma=()=>{const id=++ruleIdRef.current;set({emaRules:[...emaRules,{id,ema1:'50',ema2:'200',dir:'above'}]});};
  const remEma=(id)=>set({emaRules:emaRules.filter(r=>r.id!==id)});
  const updEma=(id,k,v)=>set({emaRules:emaRules.map(r=>r.id===id?{...r,[k]:v}:r)});
  const addPr=()=>{const id=++ruleIdRef.current;set({priceEmaRules:[...priceEmaRules,{id,ema:'50',dir:'above'}]});};
  const remPr=(id)=>set({priceEmaRules:priceEmaRules.filter(r=>r.id!==id)});
  const updPr=(id,k,v)=>set({priceEmaRules:priceEmaRules.map(r=>r.id===id?{...r,[k]:v}:r)});

  const tickerKey=(row)=>holdingsMode?row._holdingTicker:`${row.symbol}:${row.exchange}`;
  const toggleRow=(row)=>{const k=tickerKey(row);if(!k)return;setSelected(p=>{const n=new Set(p);n.has(k)?n.delete(k):n.add(k);return n;});};
  const togglePage=()=>{
    const keys=pageData.map(tickerKey).filter(Boolean);
    const all=keys.length>0&&keys.every(k=>selected.has(k));
    setSelected(p=>{const n=new Set(p);if(all)keys.forEach(k=>n.delete(k));else keys.forEach(k=>n.add(k));return n;});
  };
  const eligiblePage=pageData.filter(r=>tickerKey(r));
  const pageAllChecked=eligiblePage.length>0&&eligiblePage.every(r=>selected.has(tickerKey(r)));
  const pagePartial=!pageAllChecked&&eligiblePage.some(r=>selected.has(tickerKey(r)));

  const headers=useMemo(()=>{
    const h=[{key:'exchange',label:'Region'},{key:'symbol',label:'Symbol'},{key:'name',label:'Name'}];
    if(holdingsMode)h.push({key:'holding_local',label:'ETF %'},{key:'holding_overall',label:'Overall %'});
    BASIC_COL_DEFS.filter(c=>basicCols.includes(c.key)).forEach(c=>h.push({key:c.key,label:c.label}));
    perfCols.forEach(p=>h.push({key:'perf_'+p,label:p.toUpperCase()}));
    return h;
  },[basicCols,perfCols,holdingsMode]);

  function renderCell(row,key){
    if(key==='holding_local'||key==='holding_overall')return row[key]==null?'-':Number(row[key].toFixed(4))+'%';
    if(key==='exchange'){if(!row.exchange)return '-';const s=row.exchange==='AU'?{background:'var(--info-dim)',color:'var(--info)'}:{background:'var(--success-dim)',color:'var(--success)'};return <span style={{...S.badge,...s}}>{row.exchange}</span>;}
    if(key==='symbol'&&holdingsMode)return <span style={{paddingLeft:Math.min(row._holdingDepth,10)*14}}>
      {row._holdingChildren?<button type="button" style={{border:0,background:'none',color:'var(--accent)',cursor:'pointer',width:'20px'}}
        aria-expanded={!!row._holdingExpanded} onClick={()=>onToggleHolding(row._holdingPath)}>{row._holdingExpanded?'▾':'▸'}</button>
        :<span style={{display:'inline-block',width:'20px'}}/>}
      {row._holdingTicker?<span style={{color:'var(--accent)',fontWeight:700,cursor:'pointer'}}
        onClick={()=>onTickerClick(row._holdingTicker)}>{row.symbol||'—'}</span>:<span>{row.symbol||'—'}</span>}
    </span>;
    if(key==='symbol')return <span style={{color:'var(--accent)',fontWeight:700,cursor:'pointer'}} onClick={()=>onTickerClick?onTickerClick(`${row.symbol}:${row.exchange}`):window.open(`/chart?t=${row.symbol}:${row.exchange}`,'_blank')}>{row.symbol}</span>;
    if(key==='name')return <span style={{maxWidth:'200px',overflow:'hidden',textOverflow:'ellipsis',display:'inline-block'}}>{row.name||'-'}</span>;
    if(key==='type'){const tm={equity:{background:'var(--success-dim)',color:'var(--success)'},etf:{background:'var(--accent-dim)',color:'var(--accent)'}};return <span style={{...S.badge,...(tm[row.asset_type]||{})}}>{row.asset_type||'-'}</span>;}
    if(key==='sector')return <span style={{fontSize:'11px'}}>{row.sector||'-'}</span>;
    if(key==='industry')return <span style={{fontSize:'11px'}}>{row.industry||'-'}</span>;
    if(key==='industry_group')return <span style={{fontSize:'11px'}}>{row.industry_group||'-'}</span>;
    if(key==='sub_industry')return <span style={{fontSize:'11px'}}>{row.sub_industry||'-'}</span>;
    if(key==='idx_tracked')return <span style={{fontSize:'11px'}}>{row.idx_tracked||'-'}</span>;
    if(key==='provider_name')return <span style={{fontSize:'11px'}}>{row.provider_name||'-'}</span>;
    if(key==='expense_ratio')return fmtMer(row.expense_ratio);
    if(key==='market_cap')return fmtMktCap(row.market_cap);
    if(key==='last_price')return fmtPrice(row.last_price);
    if(key==='pe_ratio')return fmt(row.pe_ratio,1);
    if(key==='dividend_yield')return row.dividend_yield==null?'-':fmt(row.dividend_yield)+'%';
    if(key==='volume')return fmtVol(row.volume);
    if(key==='gearing_type'){
      const gt=row.gearing_type||'standard';
      const map={
        standard:          {bg:'var(--success-dim)',color:'var(--success)',label:'Standard'},
        fixed_leveraged:   {bg:'var(--warning-dim)',color:'var(--warning)',label:'Fixed Lev'},
        fixed_inverse:     {bg:'var(--error-dim)',color:'var(--error)',label:'Inverse'},
        floating_leveraged:{bg:'var(--accent-dim)',color:'var(--accent-hover)',label:'Float Lev'},
      };
      const c=map[gt]||map.standard;
      return <span style={{...S.badge,background:c.bg,color:c.color}}>{c.label}</span>;
    }
    if(key==='gearing_ratio'){
      const gr=row.gearing_ratio;
      const gt=row.gearing_type||'standard';
      if(gr==null||(gr===100&&gt==='standard'))return <span style={{color:'var(--text-muted)'}}>1x</span>;
      if(gr===100&&gt==='floating_leveraged')return <span style={{color:'var(--accent-hover)',fontWeight:600}}>?x</span>;
      const pct=Number(gr);
      const label=pct<0?`${(pct/100).toFixed(1)}x`:`${(pct/100).toFixed(2)}x`;
      const color=pct<0?'var(--down)':pct>100?'var(--warning)':'var(--text-muted)';
      return <span style={{color,fontWeight:600}}>{label}</span>;
    }
    if(key==='description')return <span style={{fontSize:'11px',whiteSpace:'normal',wordWrap:'break-word',display:'inline-block',maxWidth:'500px'}}>{row.description||'-'}</span>;
    if(key.startsWith('perf_')){
      const period=key.replace('perf_','');
      const isAnn=perfType.endsWith('_annualized');
      const base=isAnn?perfType.replace('_annualized',''):perfType;
      const raw=row[base+'_'+period];
      const val=isAnn?annualize(raw,period):raw;
      if(val==null)return '-';
      return <span style={val>=0?S.positive:S.negative}>{val.toFixed(2)}%</span>;
    }
    return '-';
  }

  function renderHeader(h){
    const isF=FILTERABLE_COLS.has(h.key);
    const si=sort.col===h.key?(sort.dir==='ASC'?' ▲':' ▼'):' ⇅';
    if(!isF)return <>{h.label}{si}</>;
    const ck=h.key;
    const excSet=colExcluded[ck]||new Set();
    const vals=colValues[ck]||new Set();
    const count=excSet.size;
    const isOpen=colDropOpen===ck;
    return (
      <div style={{position:'relative',display:'inline-flex',alignItems:'center',gap:'4px'}}>
        <span onClick={()=>handleSort(h.key)}>{h.label}{si}</span>
        <span
          onClick={e=>{e.stopPropagation();setColDropOpen(isOpen?null:ck);}}
          style={{cursor:'pointer',fontSize:'10px',padding:'1px 5px',borderRadius:'3px',background:count>0?'var(--warning-dim)':'rgba(255,255,255,0.15)',color:count>0?'var(--warning)':'var(--on-accent)',fontWeight:count>0?700:400}}
          title="Filter values"
        >▾{count>0?` (${count})`:''}</span>
        {isOpen&&(
          <ColFilterDropdown
            values={vals}
            excluded={excSet}
            onToggle={(v)=>toggleColExclude(ck,v,[...vals])}
            onClear={()=>clearColExclude(ck)}
            onClose={()=>setColDropOpen(null)}
          />
        )}
      </div>
    );
  }

  function pageButtons(){
    const btns=[],delta=2;
    const left=Math.max(2,page-delta),right=Math.min(totalPages-1,page+delta);
    btns.push(1);
    if(left>2)btns.push('...');
    for(let i=left;i<=right;i++)btns.push(i);
    if(right<totalPages-1)btns.push('...');
    if(totalPages>1)btns.push(totalPages);
    return btns;
  }

  return (
    <div style={{flex:1,display:'flex',flexDirection:'column',overflow:'hidden',minHeight:0}}>
      {/* FILTERS & COLUMNS */}
      <div style={{flexShrink:0}}>
        <div style={S.advHeader} onClick={()=>set({advOpen:!advOpen})}>
          <span>⚙ FILTERS & COLUMNS</span>
          <span>{advOpen?'▲':'▼'}</span>
        </div>
        {advOpen&&(
          <div style={S.advContent}>
            {holdingsMode&&<div>
              <strong style={{fontSize:'12px',color:'var(--text)'}}>Holding weight filters</strong>
              <div style={{...S.filterGrid,marginTop:'8px'}}>
                {[['ETF %','holding_min','holding_max'],['Overall %','overall_min','overall_max']].map(([label,min,max])=>(
                  <div key={label}>
                    <span style={S.filterLabel}>{label}</span>
                    <div style={S.rangeRow}>
                      <input style={S.filterInput} type="number" placeholder="Min" value={filters[min]||''} onChange={e=>setF(min,e.target.value)}/>
                      <span style={{color:'var(--text-muted)'}}>to</span>
                      <input style={S.filterInput} type="number" placeholder="Max" value={filters[max]||''} onChange={e=>setF(max,e.target.value)}/>
                    </div>
                  </div>
                ))}
              </div>
            </div>}
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'20px'}}>
              <div>
                <strong style={{fontSize:'12px',display:'block',marginBottom:'8px',color:'var(--text)'}}>Basic Columns</strong>
                <div style={S.colGrid}>
                  {BASIC_COL_DEFS.map(c=><button key={c.key} style={S.colToggle(basicCols.includes(c.key))} onClick={()=>toggleBasicCol(c.key)}>{c.label}</button>)}
                </div>
              </div>
              <div>
                <strong style={{fontSize:'12px',display:'block',marginBottom:'8px',color:'var(--text)'}}>Performance Columns</strong>
                <div style={S.colGrid}>
                  {PERF_PERIODS.map(p=><button key={p} style={S.colToggle(perfCols.includes(p))} onClick={()=>togglePerfCol(p)}>{p.toUpperCase()}</button>)}
                </div>
                <div style={{display:'flex',gap:'16px',marginTop:'10px',flexWrap:'wrap'}}>
                  {[['growth','Growth (Cumul.)'],['growth_annualized','Growth (Ann.)'],['drip','Total Return (Cumul.)'],['drip_annualized','Total Return (Ann.)']].map(([v,l])=>(
                    <label key={v} style={{fontSize:'12px',display:'flex',alignItems:'center',gap:'4px',cursor:'pointer'}}>
                      <input type="radio" name={`perfType-${_id}`} value={v} checked={perfType===v} onChange={()=>set({perfType:v})} />{l}
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div>
              <strong style={{fontSize:'12px',display:'block',marginBottom:'10px',color:'var(--text)'}}>Data Filters</strong>
              <div style={S.filterGrid}>
                <div>
                  <span style={S.filterLabel}>MER % (ETFs)</span>
                  <div style={S.rangeRow}>
                    <input style={S.filterInput} placeholder="Min" type="number" value={filters.mer_min} onChange={e=>setF('mer_min',e.target.value)} />
                    <span style={{fontSize:'11px',color:'var(--text-muted)'}}>to</span>
                    <input style={S.filterInput} placeholder="Max" type="number" value={filters.mer_max} onChange={e=>setF('mer_max',e.target.value)} />
                  </div>
                </div>
                <div>
                  <div style={{display:'flex',alignItems:'center',gap:'6px',marginBottom:'4px'}}>
                    <span style={S.filterLabel}>Market Cap</span>
                    <button style={{...S.colToggle(mcapMode==='range'),padding:'2px 8px',fontSize:'10px'}} onClick={()=>set({mcapMode:'range',mcapExcluded:new Set()})}>Range</button>
                    <button style={{...S.colToggle(mcapMode==='tiers'),padding:'2px 8px',fontSize:'10px'}} onClick={()=>set({mcapMode:'tiers',filters:{...filters,mcap_min:'',mcap_max:''}})}>Tiers</button>
                  </div>
                  {mcapMode==='range'?(
                    <div style={S.rangeRow}>
                      <input style={S.filterInput} placeholder="Min $M" type="number" value={filters.mcap_min} onChange={e=>setF('mcap_min',e.target.value)} />
                      <span style={{fontSize:'11px',color:'var(--text-muted)'}}>to</span>
                      <input style={S.filterInput} placeholder="Max $M" type="number" value={filters.mcap_max} onChange={e=>setF('mcap_max',e.target.value)} />
                    </div>
                  ):(
                    <div>
                      <div style={{display:'flex',gap:'12px',marginBottom:'6px'}}>
                        <label style={{fontSize:'11px',display:'flex',alignItems:'center',gap:'4px',cursor:'pointer',color:'var(--text-muted)'}}>
                          <input type="radio" name={`td-${_id}`} checked={mcapTierDefs==='us'} onChange={()=>set({mcapTierDefs:'us',mcapExcluded:new Set()})} />US
                        </label>
                        <label style={{fontSize:'11px',display:'flex',alignItems:'center',gap:'4px',cursor:'pointer',color:'var(--text-muted)'}}>
                          <input type="radio" name={`td-${_id}`} checked={mcapTierDefs==='asx'} onChange={()=>set({mcapTierDefs:'asx',mcapExcluded:new Set()})} />ASX
                        </label>
                      </div>
                      <div style={{display:'flex',flexWrap:'wrap',gap:'5px'}}>
                        {mcapTiers.map(t=>(
                          <button key={t.key} style={{...S.colToggle(!mcapExcluded.has(t.key)),padding:'3px 8px',fontSize:'10px'}} onClick={()=>toggleMcapTier(t.key)} title={mcapTierLabel(t)}>
                            {t.label} <span style={{fontSize:'9px',opacity:0.8}}>{mcapTierLabel(t)}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                {[{label:'Price ($)',mn:'price_min',mx:'price_max'},{label:'RSI (14)',mn:'rsi_min',mx:'rsi_max'},{label:'Stochastic %K',mn:'stoch_min',mx:'stoch_max'},{label:'Div Yield %',mn:'yield_min',mx:'yield_max'}].map(({label,mn,mx})=>(
                  <div key={mn}>
                    <span style={S.filterLabel}>{label}</span>
                    <div style={S.rangeRow}>
                      <input style={S.filterInput} placeholder="Min" type="number" value={filters[mn]} onChange={e=>setF(mn,e.target.value)} />
                      <span style={{fontSize:'11px',color:'var(--text-muted)'}}>to</span>
                      <input style={S.filterInput} placeholder="Max" type="number" value={filters[mx]} onChange={e=>setF(mx,e.target.value)} />
                    </div>
                  </div>
                ))}
                <div>
                  <span style={S.filterLabel}>Volume (min)</span>
                  <input style={S.filterFull} placeholder="e.g. 100000" type="number" value={filters.volume_min} onChange={e=>setF('volume_min',e.target.value)} />
                </div>
                <div>
                  <span style={S.filterLabel}>Bollinger Band</span>
                  <select style={S.filterSelect} value={filters.bb_position} onChange={e=>setF('bb_position',e.target.value)}>
                    <option value="">-- No Filter --</option>
                    <option value="above_upper">Above Upper Band</option>
                    <option value="below_lower">Below Lower Band</option>
                    <option value="inside">Inside Bands</option>
                  </select>
                </div>
                <div>
                  <span style={S.filterLabel}>Gearing Type</span>
                  <div style={{display:'flex',flexWrap:'wrap',gap:'5px',marginTop:'4px'}}>
                    {[
                      ['standard',           'Standard',          'var(--success-dim)','var(--success)'],
                      ['fixed_leveraged',    'Fixed Leveraged',   'var(--warning-dim)','var(--warning)'],
                      ['fixed_inverse',      'Fixed Inverse',     'var(--error-dim)','var(--error)'],
                      ['floating_leveraged', 'Floating Leveraged','var(--accent-dim)','var(--accent-hover)'],
                    ].map(([key,label,bg,color])=>{
                      const active=(filters.gearing_types||new Set(['standard','fixed_leveraged','fixed_inverse','floating_leveraged'])).has(key);
                      return (
                        <button key={key}
                          style={{padding:'3px 10px',fontSize:'11px',fontWeight:600,border:`1px solid ${color}`,borderRadius:'20px',cursor:'pointer',background:active?bg:'var(--surface)',color:active?color:'var(--text-muted)',fontFamily:"'JetBrains Mono',monospace",userSelect:'none'}}
                          onClick={()=>{
                            const cur=new Set(filters.gearing_types||new Set(['standard','fixed_leveraged','fixed_inverse','floating_leveraged']));
                            cur.has(key)?cur.delete(key):cur.add(key);
                            setF('gearing_types',cur);
                          }}
                        >{label}</button>
                      );
                    })}
                  </div>
                  <div style={{fontSize:'10px',color:'var(--text-muted)',marginTop:'4px'}}>Click to toggle — highlighted = included</div>
                </div>
              </div>
            </div>

            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'20px'}}>
              <div>
                <div style={{display:'flex',gap:'8px',alignItems:'center',marginBottom:'8px'}}>
                  <strong style={{fontSize:'12px',color:'var(--text)'}}>EMA Crossover Rules</strong>
                  <button style={S.addRuleBtn} onClick={addEma}>+ Add</button>
                </div>
                {emaRules.map(r=>(
                  <div key={r.id} style={S.ruleRow}>
                    <select style={S.ruleSelect} value={r.ema1} onChange={e=>updEma(r.id,'ema1',e.target.value)}>{EMA_OPTIONS.map(o=><option key={o} value={o}>EMA {o}</option>)}</select>
                    <select style={S.ruleSelect} value={r.dir}  onChange={e=>updEma(r.id,'dir', e.target.value)}><option value="above">above</option><option value="below">below</option></select>
                    <select style={S.ruleSelect} value={r.ema2} onChange={e=>updEma(r.id,'ema2',e.target.value)}>{EMA_OPTIONS.map(o=><option key={o} value={o}>EMA {o}</option>)}</select>
                    <button style={S.removeBtn} onClick={()=>remEma(r.id)}>✕</button>
                  </div>
                ))}
              </div>
              <div>
                <div style={{display:'flex',gap:'8px',alignItems:'center',marginBottom:'8px'}}>
                  <strong style={{fontSize:'12px',color:'var(--text)'}}>Price vs EMA Rules</strong>
                  <button style={S.addRuleBtn} onClick={addPr}>+ Add</button>
                </div>
                {priceEmaRules.map(r=>(
                  <div key={r.id} style={S.ruleRow}>
                    <span style={{fontSize:'12px'}}>Price</span>
                    <select style={S.ruleSelect} value={r.dir} onChange={e=>updPr(r.id,'dir',e.target.value)}><option value="above">above</option><option value="below">below</option></select>
                    <select style={S.ruleSelect} value={r.ema} onChange={e=>updPr(r.id,'ema',e.target.value)}>{EMA_OPTIONS.map(o=><option key={o} value={o}>EMA {o}</option>)}</select>
                    <button style={S.removeBtn} onClick={()=>remPr(r.id)}>✕</button>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div style={{display:'flex',gap:'8px',alignItems:'center',marginBottom:'8px'}}>
                <strong style={{fontSize:'12px',color:'var(--text)'}}>Text Search Rules</strong>
                <button style={S.addRuleBtn} onClick={addSR}>+ Add Rule</button>
              </div>
              {searchRules.map(r=>(
                <div key={r.id} style={S.ruleRow}>
                  <select style={S.ruleSelect} value={r.connector} onChange={e=>updSR(r.id,'connector',e.target.value)}>
                    <option value="AND">AND</option><option value="OR">OR</option><option value="NOT">NOT</option>
                  </select>
                  <select style={S.ruleSelect} value={r.field} onChange={e=>updSR(r.id,'field',e.target.value)}>
                    <option value="all">All Fields</option><option value="symbol">Symbol</option>
                    <option value="name">Name</option><option value="sector">Sector</option>
                    <option value="industry">Industry</option><option value="industry_group">Industry Group</option>
                    <option value="sub_industry">Sub-Industry</option><option value="description">Description</option>
                    <option value="idx_tracked">Index</option><option value="provider_name">Provider</option>
                  </select>
                  <select style={S.ruleSelect} value={r.op} onChange={e=>updSR(r.id,'op',e.target.value)}>
                    <option value="contains">Contains</option><option value="equals">Equals</option>
                    <option value="starts">Starts With</option><option value="ends">Ends With</option>
                  </select>
                  <input style={{...S.filterInput,width:'160px'}} placeholder="Search term..." value={r.value} onChange={e=>updSR(r.id,'value',e.target.value)} />
                  <label style={{fontSize:'11px',display:'flex',alignItems:'center',gap:'3px',cursor:'pointer'}}>
                    <input type="checkbox" checked={r.caseSensitive} onChange={e=>updSR(r.id,'caseSensitive',e.target.checked)} /> Aa
                  </label>
                  <button style={S.removeBtn} onClick={()=>remSR(r.id)}>✕</button>
                </div>
              ))}
            </div>

            <div style={{display:'flex',justifyContent:'flex-end'}}>
              <button style={S.btnSecondary} onClick={clearFilters}>Clear All Filters</button>
            </div>
          </div>
        )}
      </div>

      {/* Table */}
      <div style={S.tableArea}>
        {missingCount>0&&<div style={S.missingNote}>⚠ {missingCount} result{missingCount!==1?'s':''} excluded due to missing technical data</div>}
        <div style={S.statsBar}>
          <span><strong style={{color:'var(--accent)',fontSize:'16px'}}>{sorted.length.toLocaleString()}</strong> results of {allData.length.toLocaleString()} {holdingsMode?'visible holdings':'instruments'}{holdingsMode&&<span> · {allData.filter(r=>r._scannerAvailable).length} with scanner statistics</span>}</span>
          <div style={{display:'flex',alignItems:'center',gap:'8px',fontSize:'12px'}}>
            <span>Rows:</span>
            <input type="number" min="10" max="500" value={rowsPerPage} onChange={e=>set({rowsPerPage:Math.max(10,parseInt(e.target.value)||50),page:1})} style={{width:'60px',padding:'3px 6px',border:'1px solid var(--border)',borderRadius:'4px',fontSize:'12px',background:'var(--bg)',color:'var(--text)'}} />
            <span>Page {page} of {totalPages}</span>
          </div>
        </div>
        {selected.size>0&&(
          <div style={S.selBar}>
            <span style={S.selCount}>{selected.size} selected</span>
            {onTickerClick&&<button style={S.btnSm} onClick={()=>Array.from(selected).forEach(t=>onTickerClick(t))}>Open Selected</button>}
            <button style={S.btnSm} onClick={()=>setShowModal(true)}>Add to List</button>
            <button style={{...S.btnGhost,fontSize:'12px'}} onClick={()=>setSelected(new Set())}>Clear</button>
          </div>
        )}
        <div style={S.tableWrap}>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.thCheck} onClick={togglePage}>
                  <input type="checkbox" readOnly checked={pageAllChecked} disabled={eligiblePage.length===0} ref={el=>{if(el)el.indeterminate=pagePartial;}} style={{cursor:'pointer'}} />
                </th>
                {headers.map(h=>(
                  <th key={h.key} style={S.th} onClick={()=>{if(!FILTERABLE_COLS.has(h.key))handleSort(h.key);}}>
                    {renderHeader(h)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pageData.length===0
                ?<tr><td colSpan={headers.length+1} style={{...S.td,textAlign:'center',color:'var(--text-muted)',padding:'40px'}}>No results found.</td></tr>
                :pageData.map((row,i)=>{
                  const k=tickerKey(row);
                  const checked=selected.has(k);
                  return (
                    <tr key={holdingsMode?row._holdingPath:k}
                      style={{background:checked?'var(--accent-dim)':i%2===0?'var(--surface)':'var(--bg)',color:holdingsMode&&!k?'var(--text-muted)':'var(--text)'}}
                      onMouseEnter={e=>{if(!checked)e.currentTarget.style.background='var(--accent-dim)';}}
                      onMouseLeave={e=>{if(!checked)e.currentTarget.style.background=i%2===0?'var(--surface)':'var(--bg)';}}
                    >
                      <td style={S.tdCheck} onClick={()=>toggleRow(row)}><input type="checkbox" readOnly checked={checked} disabled={!k} style={{cursor:k?'pointer':'default'}} /></td>
                      {headers.map(h=><td key={h.key} style={{...S.td,whiteSpace:h.key==='description'?'normal':'nowrap'}}>{renderCell(row,h.key)}</td>)}
                    </tr>
                  );
                })
              }
            </tbody>
          </table>
        </div>
        {totalPages>1&&(
          <div style={S.pagination}>
            <button style={S.pageBtn(false)} onClick={()=>set({page:1})} disabled={page===1}>«</button>
            <button style={S.pageBtn(false)} onClick={()=>set({page:Math.max(1,page-1)})} disabled={page===1}>‹</button>
            <div style={{display:'flex',gap:'4px'}}>
              {pageButtons().map((b,i)=>b==='...'
                ?<span key={`e${i}`} style={{padding:'0 4px',color:'var(--text-muted)'}}>…</span>
                :<button key={b} style={S.pageBtn(page===b)} onClick={()=>set({page:b})}>{b}</button>
              )}
            </div>
            <button style={S.pageBtn(false)} onClick={()=>set({page:Math.min(totalPages,page+1)})} disabled={page===totalPages}>›</button>
            <button style={S.pageBtn(false)} onClick={()=>set({page:totalPages})} disabled={page===totalPages}>»</button>
          </div>
        )}
      </div>

      {showModal&&<AddToListModal tickers={Array.from(selected)} onClose={()=>setShowModal(false)} onListCreated={onListCreated} />}
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function MarketScanner({ embedded=false, onTickerClick=null, onListCreated=null, refreshKey=0, holdings=null, holdingsName='' }) {
  const ruleIdRef = useRef(1);
  const holdingsMode = holdings !== null;
  const [expandedHoldings, setExpandedHoldings] = useState({});
  const toggleHolding = useCallback(path=>setExpandedHoldings(current=>({...current,[path]:!current[path]})),[]);


  useEffect(()=>{ if(!embedded) document.title='Market Scanner | chart.ninja'; },[embedded]);

  const [allData,       setAllData]       = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [lists,         setLists]         = useState([]);
  const [activeTab,     setActiveTab]     = useState(holdingsMode?'holdings':'total');
  const [tabStates,     setTabStates]     = useState(() => holdingsMode?{holdings:defaultTabState('holdings')}:{total:defaultTabState('total')});
  const [listTickers,   setListTickers]   = useState({});
  const [loadedListIds, setLoadedListIds] = useState(new Set());

  const holdingsData = useMemo(()=>{
    if(!holdingsMode)return [];
    const byTicker=new Map(allData.map(row=>[`${row.symbol}:${row.exchange}`,row]));
    const out=[];
    const walk=(items,parent,depth,prefix)=>{
      (items||[]).forEach((holding,index)=>{
        const path=prefix+'/'+(holding.holding_id||index);
        const local=holding.weight==null||holding.weight===''?null:Number(holding.weight);
        const overall=local==null||!Number.isFinite(local)||parent==null?null:parent*local/100;
        const matched=!!(holding.matched_instrument_id&&holding.matched_symbol&&holding.matched_exchange
          &&(holding.holding_type||'').toLowerCase()!=='cash');
        const ticker=matched?`${holding.matched_symbol}:${holding.matched_exchange}`:null;
        const stats=ticker?byTicker.get(ticker):null;
        const children=holding.holdings||[];
        out.push({
          ...(stats||{}),
          symbol:holding.holding_symbol||holding.matched_symbol||'',
          exchange:holding.matched_exchange||(stats?.exchange||''),
          name:holding.holding_name||stats?.name||'',
          asset_type:stats?.asset_type||holding.holding_type||'other',
          holding_local:Number.isFinite(local)?local:null,
          holding_overall:overall,
          _holdingPath:path,
          _holdingParent:prefix,
          _holdingDepth:depth,
          _holdingChildren:children.length>0,
          _holdingExpanded:!!expandedHoldings[path],
          _holdingTicker:ticker,
          _scannerAvailable:!!stats,
        });
        if(expandedHoldings[path])walk(children,overall,depth+1,path);
      });
    };
    walk(holdings,100,0,'');
    return out;
  },[holdingsMode,holdings,allData,expandedHoldings]);

  const tabs = useMemo(()=>{
    if(holdingsMode)return [{id:'holdings',label:holdingsName||'ETF Holdings'}];
    const t=[{id:'total',label:'TotalMarket'}];
    lists.filter(l=>loadedListIds.has(l.id)).forEach(l=>t.push({id:`list-${l.id}`,label:l.name,listId:l.id}));
    return t;
  },[lists,loadedListIds,holdingsMode,holdingsName]);

  useEffect(()=>{
    setLoading(true);
    Promise.all([api.get('/scanner/data'),...(holdingsMode?[]:[api.get('/lists')])])
      .then(([s,l])=>{ setAllData(dedupeRows(Array.isArray(s.data)?s.data:[])); if(l)setLists(l.data.lists||[]); })
      .catch(()=>{}).finally(()=>setLoading(false));
  },[refreshKey,holdingsMode]);

  const handleToggleList = useCallback(async(list)=>{
    const tabId=`list-${list.id}`;
    if(loadedListIds.has(list.id)){
      setLoadedListIds(p=>{const n=new Set(p);n.delete(list.id);return n;});
      setActiveTab(cur=>cur===tabId?'total':cur);
    }else{
      try{
        const r=await api.get(`/lists/${list.id}/items`);
        const ts=new Set((r.data.items||[]).map(i=>i.ticker));
        setListTickers(p=>({...p,[tabId]:ts}));
        setLoadedListIds(p=>{const n=new Set(p);n.add(list.id);return n;});
        setTabStates(p=>p[tabId]?p:{...p,[tabId]:defaultTabState(tabId)});
        setActiveTab(tabId);
      }catch(_){}
    }
  },[loadedListIds]);

  const handleRefreshTab = useCallback(async(tabId)=>{
    if(tabId==='total'||tabId==='holdings'){
      setLoading(true);
      api.get('/scanner/data')
        .then(r=>setAllData(dedupeRows(Array.isArray(r.data)?r.data:[])))
        .catch(()=>{}).finally(()=>setLoading(false));
    }else{
      const listId=parseInt(tabId.replace('list-',''));
      try{
        const r=await api.get(`/lists/${listId}/items`);
        const ts=new Set((r.data.items||[]).map(i=>i.ticker));
        setListTickers(p=>({...p,[tabId]:ts}));
      }catch(_){}
    }
  },[]);

  const handleCloseTab=useCallback((tabId)=>{
    const listId=parseInt(tabId.replace('list-',''));
    setLoadedListIds(p=>{const n=new Set(p);n.delete(listId);return n;});
    setActiveTab(cur=>cur===tabId?'total':cur);
  },[]);

  const updateTabState=useCallback((tabId,updater)=>{
    setTabStates(p=>({...p,[tabId]:updater(p[tabId]||defaultTabState(tabId))}));
  },[]);

  const handleListCreated=useCallback((list)=>{
    setLists(p=>p.find(l=>l.id===list.id)?p:[...p,list]);
    if(onListCreated)onListCreated(list);
  },[onListCreated]);


  return (
    <div style={S.page(embedded)}>
      <style>{KEYFRAMES}</style>
      {!embedded&&<Navbar rightLinks={[{ label: 'Workspace ↗', href: '/workspace' }]} />}
      <div style={S.body(embedded)}>
        <TabStrip
          tabs={tabs} activeTab={activeTab}
          onSelect={setActiveTab} onClose={handleCloseTab} onRefresh={handleRefreshTab}
        />
        {holdingsMode?
          <div style={S.listBar}>
            <span style={S.listBarLabel}>ETF Holdings</span>
            <span style={S.listBarBtn(true)}>{holdingsName||'Holdings'}</span>
            <a href="/etf" style={{marginLeft:'auto',color:'var(--accent)',fontSize:'11px'}}>ETF list</a>
          </div>
          :<ListSelector lists={lists} loadedListIds={loadedListIds} onToggleList={handleToggleList}/>}

        {loading?(
          <div style={S.loading}>
            <div style={{width:'32px',height:'32px',border:'3px solid var(--border)',borderTopColor:'var(--accent)',borderRadius:'50%',animation:'spin 0.7s linear infinite'}} />
            Loading instruments...
          </div>
        ):tabs.map(t=>(
          <div key={t.id} style={{flex:1,display:'flex',flexDirection:'column',minHeight:0,overflow:'hidden',visibility:t.id===activeTab?'visible':'hidden',position:t.id===activeTab?'relative':'absolute',width:'100%',height:'100%'}}>
            <ScannerView
              allData={holdingsMode?holdingsData:allData}
              listTickers={holdingsMode||t.id==='total'?null:(listTickers[t.id]||new Set())}
              tabState={tabStates[t.id]||defaultTabState(t.id)}
              onStateChange={(u)=>updateTabState(t.id,u)}
              onTickerClick={onTickerClick}
              onListCreated={handleListCreated}
              ruleIdRef={ruleIdRef}
              holdingsMode={holdingsMode}
              onToggleHolding={toggleHolding}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
